/** @module Specification/PartMaterialColor/Matrix */
define([
	"dojo/_base/declare",
	"underscore",
	"csi",
	"csi/table/matrix/Path",
	"csi/table/TableComparisonData",
	"csi/xml",
	"Specification/BOM/service/bomCompareService"
], function(declare, _, csi, Path, {COMPARE_KEY_INDEX}, xml, {compareBomColors, isNewCompareColor, isOldCompareColor}) {

	/**
	 * NOTE: Please make sure that any attributes used in this file on PartMaterial(Color) are included in Fmt.Attr.List property in StructureItem.__Print__ View
	 */

	const HPM = "_HPMColor";
	const ID_BOM_COLOR = "PMCBOMColor";
	const ID_COLOR = "PMCColor";
	const PRODUCT_SOURCE = "ProductSource";

	/**
	 * Calculate mapping of BOM colors to the PartMaterialColor that applies to it for the given query result
	 * @param {Context} ctx
	 * @param {Result} qr
	 * @return {Object<string,string>} map of BOM Color URL (matrix column) to PartMaterialColor URL
	 */
	function _getColumnMap(ctx, qr) {
		// nothing to do if at root of table or on a group
		if (qr.isRoot() || qr.isGroup()) {
			return null;
		}

		// return if column map is already cached
		const hpmMap = ctx[HPM] = ctx[HPM] || {};
		let columnMap = hpmMap[qr.id];
		if (columnMap) {
			return columnMap;
		}

		const pm = qr.getNode();
		// if performing BOM Compare with certain filters, these might not be available, so compact them out
		const pmcs = _.compact(ctx.cache.getNodes(pm.PartMaterialColors));

		// determine mapping from BOM Color to PartMaterialColor
		const obj = csi.hashArray(pmcs, ID_BOM_COLOR, "$URL");

		if (qr.level.bMain) {
			// if at main level, then PartMaterialColors will directly reference BOM color
			columnMap = obj;
		} else {
			// if not at main level, then PartMaterialColors will reference a ProductColor selected in parent PartMaterial, rather than the column's BOM Color URL
			const parentMap = _getColumnMap(ctx, qr.parentResult);

			columnMap = {};

			_.each(parentMap, function(pmcUrl, bomColorUrl) {
				const pmc = ctx.cache.get(pmcUrl);
				const pm = pmc && ctx.getNodeParent(pmc);

				if (pm && pm.PartMaterialColorsFiltered.includes(pmcUrl)) {
					const colorUrl = pmc[ID_COLOR];
					columnMap[bomColorUrl] = obj[colorUrl];
				}
			});
		}

		// cache for later
		hpmMap[qr.id] = columnMap;

		return columnMap;
	}

	return declare(Path, {
		bAllowColumnFilter: true,
		availableColumnStoreProperty: "bomColorStore",
		columnPath: "Child:BOMProductColors",
		restrictQuery: true,
		selectedColumnValueProperty: "bomActiveColors",

		/**
		 * @override
		 * Return data to enter into the column nodes store
		 */
		getColumnStoreData(ctx, columnNodes) {
			const {colorCompareData} = ctx.view;

			if (colorCompareData) {
				columnNodes = columnNodes.filter((node) => !isOldCompareColor(colorCompareData, node.$URL));
			}

			return columnNodes;
		},

		/**
		 * @override
		 * Return constant key to indicate to compare by index - eventually would like to remove color mapping from
		 * TableComparisonData and purely use a compare key
		 */
		getCompareKeyForMergedCells(ctx, spec, index) {
			return COMPARE_KEY_INDEX;
		},

		/**
		 * @override
		 * Return true if column represents a new color
		 */
		isNewComparisonColumn(ctx, columnUrl) {
			return isNewCompareColor(ctx.view.colorCompareData, columnUrl);
		},

		/**
		 * @override
		 * Return true if column represents an old color
		 */
		isOldComparisonColumn(ctx, columnUrl) {
			return isOldCompareColor(ctx.view.colorCompareData, columnUrl);
		},

		/**
		 * @override
		 * No need to render the Color subheader if it is the only one
		 */
		shouldRenderLoneSubheader: function(subSpec) {
			var h = subSpec.heading;
			return !((h.m_id === ID_COLOR) || ((h.m_id === xml.ATTR_NAME) && (h.m_path === xml.path(ID_COLOR))));
		},

		/**
		 * @override
		 * If view indicates a separate set of colors, then use intersection of them with the BOM's set of colors
		 */
		_getColumnNodes: function(ctx, h) {
			const {cache, view} = ctx;
			const {bomActiveColors, bomAvailableColorUrls, compareData} = view;
			let bomColors = this.inherited(arguments);

			if (bomAvailableColorUrls) {
				const bomColorUrls = _.pluck(bomColors, "$URL");
				const urls = _.intersection(bomAvailableColorUrls, bomColorUrls);
				bomColors = cache.getNodes(urls);
			}

			const colorData = view.colorCompareData = compareData ? compareBomColors(compareData, bomColors) : null;

			// add colors from comparison view that are not in this view to matrix so long as not looking at specific set of colors
			if (colorData && _.isEmpty(bomActiveColors)) {
				const oldColors = cache.getNodes(colorData.oldColorUrls);
				bomColors.push(...oldColors);
			}
			//Filter logic for color allocation
			if(view.urlRoot){
				 const filterBomColors = this._filterBomColorsForProductSourceAllocation(bomColors, cache, view);
				 return filterBomColors;
			}else{
				return bomColors;
			}
		},

		/**
		 *
		 * Method to handle product source color allocation logic to filter bomcolors according to the allocation
		 * this is only necessary when bom is linked to a product source and color allocation is enable
		 * @param {*} bomColors
		 * @returns filtered bomResults or empty collection
		 */
		_filterBomColorsForProductSourceAllocation(bomColors, cache, view) {
			const productBomRevisionNode = cache.get(view.urlRoot);
			const productBomNode = cache.get(productBomRevisionNode.__Parent__);
			let parent = cache.get(productBomNode.__Parent__);
			let colorSourceUrls;
			if (parent?.$Type === PRODUCT_SOURCE) {
				colorSourceUrls = parent?.ColorSources;			
			} else {
				colorSourceUrls = cache.get(productBomNode.ForProductSource)?.ColorSources;
			}
			if (colorSourceUrls?.length && parent?.ColorAllocationStatus === "ColorAllocationStatus:Allocation WIP") {
				const sourceColorwayUrls = colorSourceUrls.map((url) => cache.get(url).Colorway);
				const set = new Set(sourceColorwayUrls);
				bomColors = bomColors.filter((colorway) => set.has(colorway.$URL));
			}
			return bomColors;
		},

		/**
		 * Hide column if PartMaterialColor isn't in PartMaterialColorsFiltered
		 * list, but keep HideProductColor heading visible
		 * @override
		 */
		_getSpecNodes: function(ctx, spec, nodes) {
			if (!nodes.length ||
				(spec.heading.m_nameOrig == "HideProductColor") ||
				_.contains(ctx.getNodeParent().PartMaterialColorsFiltered, nodes[0].$URL)
			) {
				return nodes;
			}

			return [];
		},


		/**
		 * @override
		 * Return array of at most 1 PartMaterialColor node based on OnlyForColors list (if any) and
		 * the PartMaterialColor that applies to the current column. In lower levels of the Multi-level Placements
		 * table, it gets more complicated as the PartMaterialColor references a ProductColor that is the selected
		 * color of its parent PartMaterial.
		 */
		_getRowNodes: function(ctx, qr, c, node) {
			const columnMap = _getColumnMap(ctx, qr);
			const pmcUrl = columnMap[c.columnUrl];
			const pmc = ctx.cache.getNode(pmcUrl);
			return pmc ? [pmc] : [];
		}
	});
});